import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import type { MeResponse, Role } from "@starsach/shared-types";
import type { AppConfig } from "../config";

export interface Session {
  idToken: string;
  /** Carries the aws.cognito.signin.user.admin scope: what the browser manages its own user with (section 13.3). */
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  /** Cognito's username claim - what USER_AUTH is started with for a step-up. */
  username?: string;
  /** When the user last proved who they are (id token `auth_time`, epoch seconds); step-up compares against it. */
  authTime?: number;
  /**
   * When this sign-in happened (epoch ms), set once at the callback and kept across step-ups - which renew
   * `auth_time` but are not a sign-in from the user's side. "Do later" on a nudge means "until the next of these".
   */
  signedInAt?: number;
  expiresAt: number;
}

const SIGNED_IN_AT_KEY = "starsach.signedInAt";
function signedInAtOf(): number | undefined {
  const raw = window.sessionStorage.getItem(SIGNED_IN_AT_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : undefined;
}
export function markSignedIn(): void {
  window.sessionStorage.setItem(SIGNED_IN_AT_KEY, String(Date.now()));
}

/** The tokens a fresh in-app authentication (step-up) hands back. */
export interface FreshTokens {
  IdToken?: string;
  AccessToken?: string;
  RefreshToken?: string;
  ExpiresIn?: number;
}

/**
 * ADR-0014: which identity provider to send the browser to. `idpIdentifier`
 * is an email domain registered on the provider (the login page's route);
 * `identityProvider` is the provider's name (a federated step-up, which
 * already knows it from /v1/me). Neither: Cognito's own sign-in.
 */
export interface SignInRoute {
  idpIdentifier?: string;
  identityProvider?: string;
}

export interface AuthAdapter {
  getSession(): Promise<Session | null>;
  /** Replaces the stored tokens after a step-up, so the session's auth_time is the new one. */
  replaceTokens(tokens: FreshTokens): Promise<Session | null>;
  signIn(returnTo: string, route?: SignInRoute): Promise<void>;
  completeSignIn(): Promise<string>;
  signOut(): Promise<void>;
  /**
   * New ID and access tokens from the refresh token, before or after they lapse (crmarr/templates auth pitfall 32).
   * `null` when the refresh is refused (revoked, past its life): the stored user is removed and sign-in is offered.
   */
  renew(): Promise<Session | null>;
  /** Called whenever the library loads or drops the user - a renewal included - so every request carries the current token. */
  onSessionChange(listener: (session: Session | null) => void): () => void;
}

/**
 * Cognito Hosted UI via OIDC authorization code + PKCE. Tokens live in
 * sessionStorage (cleared with the tab); the ID token is what the Portal
 * API's JWT authorizer validates.
 */
export function cognitoAuth(config: AppConfig): AuthAdapter {
  const origin = window.location.origin;
  const manager = new UserManager({
    authority: config.cognito.issuer,
    client_id: config.cognito.clientId,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: "code",
    // The last scope is what lets the access token manage its own user (devices, passkeys, password, authenticator).
    scope: "openid email profile aws.cognito.signin.user.admin",
    // The library renews before expiry with the refresh-token grant (no iframe). The tokens last fifteen minutes.
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });

  // `auth_time` is read from the token itself: oidc-client-ts filters it (and other protocol claims) out of `profile`.
  const toSession = (u: User | null): Session | null =>
    u && !u.expired && u.id_token
      ? {
          idToken: u.id_token,
          accessToken: u.access_token,
          refreshToken: u.refresh_token,
          email: u.profile.email,
          username: typeof u.profile["cognito:username"] === "string" ? u.profile["cognito:username"] : undefined,
          authTime: authTimeOf(claimsOf(u.id_token)),
          signedInAt: signedInAtOf(),
          expiresAt: (u.expires_at ?? 0) * 1000,
        }
      : null;

  const renew = async (): Promise<Session | null> => {
    try {
      return toSession(await manager.signinSilent());
    } catch {
      await manager.removeUser();
      return null;
    }
  };

  return {
    // A tab reopened after the tokens lapsed still holds a refresh token: renew before reading the session.
    getSession: async () => {
      const u = await manager.getUser();
      if (u?.expired && u.refresh_token) return renew();
      return toSession(u);
    },
    renew,
    onSessionChange: (listener) => {
      const loaded = (u: User) => listener(toSession(u));
      const unloaded = () => listener(null);
      manager.events.addUserLoaded(loaded);
      manager.events.addUserUnloaded(unloaded);
      return () => {
        manager.events.removeUserLoaded(loaded);
        manager.events.removeUserUnloaded(unloaded);
      };
    },
    replaceTokens: async (t) => {
      const current = await manager.getUser();
      if (!t.IdToken || !t.AccessToken) return toSession(current);
      const profile = claimsOf(t.IdToken) as User["profile"];
      const user = new User({
        id_token: t.IdToken,
        access_token: t.AccessToken,
        // The tab's own refresh token, never the step-up's: a step-up is an API sign-in, and with device tracking on its
        // refresh token is refused at /oauth2/token until the device is confirmed - storing it signed the person out at
        // the next renewal (reproduced on dev, 2 Oct). The step-up's refresh token is dropped.
        refresh_token: current?.refresh_token ?? t.RefreshToken,
        token_type: "Bearer",
        scope: current?.scope,
        profile,
        expires_at: Math.floor(Date.now() / 1000) + (t.ExpiresIn ?? 3600),
      });
      await manager.storeUser(user);
      return toSession(user);
    },
    signIn: (returnTo, route) =>
      manager.signinRedirect({
        state: { returnTo },
        ...(route?.idpIdentifier ? { extraQueryParams: { idp_identifier: route.idpIdentifier } } : route?.identityProvider ? { extraQueryParams: { identity_provider: route.identityProvider } } : {}),
      }),
    completeSignIn: async () => {
      const u = await manager.signinCallback();
      markSignedIn();
      const state = (u?.state ?? {}) as { returnTo?: string };
      return state.returnTo ?? "/";
    },
    signOut: async () => {
      // Clearing the tab and ending the hosted session leave the refresh token good for its whole life; revoke it first.
      const refreshToken = (await manager.getUser())?.refresh_token;
      if (refreshToken) await revokeRefreshToken(config.cognito, refreshToken);
      await manager.removeUser();
      // Cognito's discovery document has no end_session_endpoint; its logout URL is the Hosted UI's.
      const url = new URL(`https://${config.cognito.domain}/logout`);
      url.searchParams.set("client_id", config.cognito.clientId);
      url.searchParams.set("logout_uri", `${origin}/`);
      window.location.assign(url.toString());
    },
  };
}

/**
 * Cognito's /oauth2/revoke: the refresh token, and every access token it
 * issued, stop working. Best effort - a sign-out is never held back by it,
 * so a failure is swallowed and the tab is cleared regardless.
 */
export async function revokeRefreshToken(cognito: { domain: string; clientId: string }, token: string): Promise<boolean> {
  try {
    const res = await fetch(`https://${cognito.domain}/oauth2/revoke`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ token, client_id: cognito.clientId }).toString(),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Epoch seconds from the `auth_time` claim, whether the issuer wrote it as a number or a string. */
export function authTimeOf(claims: Record<string, unknown>): number | undefined {
  const raw = claims["auth_time"];
  const n = typeof raw === "number" ? raw : typeof raw === "string" && /^\d+$/.test(raw) ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

/** The id token's claims, read without verification: the API verifies; the browser only reads what it was given. */
export function claimsOf(jwt: string): Record<string, unknown> {
  try {
    const payload = jwt.split(".")[1] ?? "";
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** `mockAuth` (CLAUDE.md section 11): a fake session whose role is chosen on the login screen. */
export function mockAuth(): AuthAdapter {
  const KEY = "mock-session";
  return {
    getSession: async () => {
      const raw = window.sessionStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    },
    replaceTokens: async (t) => {
      const raw = window.sessionStorage.getItem(KEY);
      if (!raw) return null;
      const s = JSON.parse(raw) as Session;
      const next: Session = { ...s, authTime: Math.floor(Date.now() / 1000), ...(t.AccessToken ? { accessToken: t.AccessToken } : {}) };
      window.sessionStorage.setItem(KEY, JSON.stringify(next));
      return next;
    },
    signIn: async (returnTo, route) => {
      // No Hosted UI to go to: remember where the real adapter would have sent the browser, for the e2e.
      window.sessionStorage.setItem("mock-sign-in-route", JSON.stringify({ returnTo, ...route }));
      window.location.assign("/login");
    },
    completeSignIn: async () => "/",
    renew: async () => {
      const raw = window.sessionStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    },
    onSessionChange: () => () => undefined,
    signOut: async () => {
      window.sessionStorage.removeItem(KEY);
      window.location.assign("/login");
    },
  };
}

/**
 * ADR-0017: which person the mock session is. The default customer is a
 * manager and the default staff member the owner (a superadmin); `viewer`
 * is a customer holding the built-in viewer, `reader` a `gairm-reader`.
 */
export type MockPersona = "viewer" | "reader";

export function setMockSession(role: Role, organisationId?: string, environmentIds?: readonly string[], identityProvider?: string, persona?: MockPersona): void {
  const who = persona ?? role;
  const me: MeResponse & { environmentIds?: readonly string[] } = {
    subject: `mock-${who}`,
    email: `${who}@example.test`,
    role,
    organisationId,
    // Not part of /v1/me - carried in the mock "token" so the mock API can
    // apply ADR-0009's per-environment boundary without a Memberships table.
    ...(environmentIds ? { environmentIds } : {}),
    // ADR-0014: a federated user, as the pre-token trigger would have stamped them - with the name the provider mapped.
    ...(identityProvider ? { identityProvider, firstName: "Ada", lastName: "Lovelace", displayName: "Ada Lovelace" } : {}),
  };
  // The mock API reads the role back out of this "token".
  const idToken = `mock.${btoa(JSON.stringify(me))}`;
  window.sessionStorage.setItem(
    "mock-session",
    JSON.stringify({ idToken, accessToken: idToken, email: me.email, username: me.email, authTime: Math.floor(Date.now() / 1000), signedInAt: Date.now(), expiresAt: Date.now() + 8 * 3_600_000 } satisfies Session),
  );
}
